import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF5A00]" />
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password.");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed. Check credentials.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen bg-zinc-950 grid place-items-center px-6 py-24"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <Logo variant="stacked" showSubtitle={false} />
          <div className="font-heading text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
            Admin Console
          </div>
        </div>

        <h1 className="font-display text-4xl uppercase tracking-wider text-zinc-50 mb-2">
          Sign in
        </h1>
        <p className="text-zinc-400 text-sm mb-8">
          Restricted area. Vision Era Construction staff only.
        </p>

        <form
          onSubmit={submit}
          className="bg-zinc-900 border border-zinc-800 p-8 space-y-5"
        >
          <div className="space-y-2">
            <Label className="font-heading text-xs tracking-[0.25em] uppercase text-zinc-400">
              Email
            </Label>
            <Input
              type="email"
              data-testid="admin-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="bg-zinc-950 border border-zinc-800 rounded-none h-12 text-zinc-100 focus-visible:ring-1 focus-visible:ring-[#FF5A00] focus-visible:border-[#FF5A00] focus-visible:ring-offset-0"
              placeholder="admin@visioneraconstruction.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="font-heading text-xs tracking-[0.25em] uppercase text-zinc-400">
              Password
            </Label>
            <Input
              type="password"
              data-testid="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="bg-zinc-950 border border-zinc-800 rounded-none h-12 text-zinc-100 focus-visible:ring-1 focus-visible:ring-[#FF5A00] focus-visible:border-[#FF5A00] focus-visible:ring-offset-0"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-submit"
            disabled={submitting}
            className="btn-primary w-full justify-center disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" /> Sign in
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
